/**
 * "YOU HAVE UNSAVED PROGRESS" — D-EZ-103.
 *
 * The browser's leave-page warning, raised only when leaving would really lose
 * something:
 *
 *   • A HAND-ROLLED RACE in progress. Its faces live only in the dice table on
 *     this page — the device backup (`localBackup.ts`) copies FINISHED races, not
 *     a race half-rolled — so closing the tab throws away every die entered.
 *   • A LEAGUE whose latest race or settle has not reached the device backup yet
 *     (or the backup failed — private browsing, storage blocked) and has not been
 *     saved to the account since.
 *
 * Pure. The shell computes the key of what is on the workbench, records the key
 * of what was last protected (device copy or account save), and asks.
 */

export interface ProgressShape {
  leagueId: string;
  season: { seasonNumber: number; state: { races: { length: number } } } | null;
  career: { seasonNumber: number } | null;
  history: readonly unknown[];
}

/** A key for "what is on the workbench right now". Null when there is no league. */
export function progressKey(x: ProgressShape): string | null {
  if (!x.season && !x.career) return null;
  const live = x.season ? `${x.season.seasonNumber}:${x.season.state.races.length}` : '-';
  return `${x.leagueId}|${live}|${x.career?.seasonNumber ?? '-'}|${x.history.length}`;
}

export interface UnsavedInput {
  /** A hand-rolled race (exhibition or season) is on the dice table. */
  handRolling: boolean;
  /** Dice entered so far in it. */
  handFaces: number;
  /** `progressKey` of the workbench now. */
  progress: string | null;
  /** `progressKey` of what was last copied to this device or saved to the account. */
  protectedKey: string | null;
}

/** The warning to raise, or null when leaving loses nothing. */
export function unsavedWarning(x: UnsavedInput): string | null {
  if (x.handRolling) {
    return (
      `You are rolling a race by hand${x.handFaces > 0 ? ` (${x.handFaces} dice entered)` : ''}. ` +
      'Those dice are only on this page, so leaving loses them. Finished races are kept on this device.'
    );
  }
  if (x.progress !== null && x.progress !== x.protectedKey) {
    return 'Your latest race has not been copied to this device yet. Stay a moment, or use Save league in Account.';
  }
  return null;
}
